import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Dialog } from "@headlessui/react";
import { sdkWrapperURL } from "utils/api-url-list";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [otp, setOTP] = useState("");
  const [otpTimer, setOTPTimer] = useState(0);
  const [error, setError] = useState("");

  const [isHelpModalOpen, setHelpModalOpen] = useState(false);

  const sendOTP = async event => {
    try {
      event.preventDefault();
      setLoading(true);
      setError("");
      if (phoneNumber.length <= 0) throw new Error("Please enter a Phone Number");
      else if (!phoneNumber.match(/^\d{10}$/)) throw new Error("Incorrect Phone Number");
      else if (clientCode.length <= 0) throw new Error("Please enter a Client Code");
      else {
        const resp = await fetch(sdkWrapperURL("/auth/send-otp"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key: clientCode,
            action: "send",
            channel_id: "login_mobile",
            phone_no: phoneNumber,
          }),
        });
        const resp_json = await resp.json();
        if (resp_json.statusCode?.toString().startsWith("2")) {
          setOTPTimer(100);
          setOTP("");
          setPage(2);
          toast("OTP Sent Successfully", {
            type: "success",
            position: "top-center",
            hideProgressBar: false,
            draggable: false,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            closeOnClick: false,
            autoClose: 3000,
          });
        } else throw new Error(resp_json.message);
      }
    } catch (err) {
      cancelOTP();
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async event => {
    try {
      event.preventDefault();
      setLoading(true);
      setError("");
      if (otp.length <= 0) throw new Error("Please enter a OTP");
      else if (!otp.match(/[0-9]{6}/)) throw new Error("OTP should be 6 Digits");
      else {
        const resp = await fetch(sdkWrapperURL("/auth/sign-in"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key: clientCode,
            login_type: "session",
            login_method: "otp_mobile",
            phone_no: phoneNumber,
            otp: otp,
          }),
        });
        const resp_json = await resp.json();
        if (resp_json.statusCode?.toString().startsWith("2")) {
          localStorage.setItem("client_code", clientCode);
          localStorage.setItem("token", resp_json.user?.id_token);
          localStorage.setItem("emp_id", resp_json.user?.user_id);
          navigate(location.state?.from || "/", { replace: true });
        } else throw new Error(resp_json.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelOTP = () => {
    setOTPTimer(0);
    setOTP("");
    setError("");
    setPage(1);
  };

  useEffect(() => {
    let timer = undefined;
    if (typeof otpTimer === "number") {
      if (otpTimer > 0) timer = setTimeout(() => setOTPTimer(t => t - 1), 1000);
      else {
        setOTPTimer(0);
        setOTP("--- OTP Expired ---");
        setError("");
      }
    }
    return () => {
      clearTimeout(timer);
    };
  }, [otpTimer]);

  useEffect(() => {
    if (localStorage.getItem("token")?.length > 0)
      navigate(location.state?.from || "/", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return page === 0 ? (
    <div className="min-h-screen flex flex-col items-stretch justify-start bg-white">
      <img
        className="flex-shrink-0 self-start my-10 mx-6 w-32 max-h-16 object-contain object-left"
        src="https://content.careplix.com/trial_app/CAREPLIX.png"
        alt="CAREPLIX"
      />
      <img
        className="flex-[0.5] w-full max-h-[37.5vh] object-cover"
        src={process.env.PUBLIC_URL + "/images/get-started.webp"}
        alt="banner"
      />
      <div className="flex-shrink-0 p-8">
        <h1 className="text-primary text-2xl leading-tight font-semibold whitespace-pre-line">
          {"Monitor Your\nHealth Anytime,\nAnywhere"}
        </h1>
        <p className="mt-3 text-darkgray">
          Monitoring your health is now as simple as taking a selfie. Don't believe? Get started to
          see it yourself.
        </p>
        <button
          className="w-full mt-10 px-4 py-2 rounded-full bg-secondary text-white text-lg font-medium"
          type="button"
          onClick={() => setPage(1)}
        >
          Get Started
        </button>
      </div>
    </div>
  ) : (
    <div className="min-h-screen p-6 bg-white">
      {page === 2 ? (
        <>
          <h1 className="text-2xl text-secondary font-semibold">Verify OTP</h1>
          <h4 className="mt-3 text-darkgray font-light">
            Enter the 6-digits OTP sent to your phone number
          </h4>
          <form
            onSubmit={typeof otpTimer !== "number" || otpTimer <= 0 ? sendOTP : verifyOTP}
            className="my-6"
          >
            <input
              className="block w-full px-4 py-2 rounded-md outline-none border border-lightgray bg-white text-primary text-base"
              id="otp"
              name="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              placeholder="Enter 6 Digit OTP"
              value={otp}
              onChange={e => {
                if (e.target.value.length <= 6) setOTP(e.target.value);
              }}
              disabled={typeof otpTimer !== "number" || otpTimer <= 0}
            />
            {otpTimer > 0 && (
              <p className="mt-1.5 text-right text-sm text-error font-light">
                Resend OTP in&nbsp;{Math.floor(otpTimer / 60)}:
                {(otpTimer % 60).toString().padStart(2, 0)}&nbsp;secs
              </p>
            )}
            {error.length > 0 && (
              <p className="mt-4 p-3 rounded-md flex items-center bg-error/10 text-error">
                <svg className="shrink-0 h-5 w-5" viewBox="0 0 48 48" fill="currentColor">
                  <path d="M24 34q.7 0 1.175-.475.475-.475.475-1.175 0-.7-.475-1.175Q24.7 30.7 24 30.7q-.7 0-1.175.475-.475.475-.475 1.175 0 .7.475 1.175Q23.3 34 24 34Zm-1.35-7.65h3V13.7h-3ZM24 44q-4.1 0-7.75-1.575-3.65-1.575-6.375-4.3-2.725-2.725-4.3-6.375Q4 28.1 4 23.95q0-4.1 1.575-7.75 1.575-3.65 4.3-6.35 2.725-2.7 6.375-4.275Q19.9 4 24.05 4q4.1 0 7.75 1.575 3.65 1.575 6.35 4.275 2.7 2.7 4.275 6.35Q44 19.85 44 24q0 4.1-1.575 7.75-1.575 3.65-4.275 6.375t-6.35 4.3Q28.15 44 24 44Zm.05-3q7.05 0 12-4.975T41 23.95q0-7.05-4.95-12T24 7q-7.05 0-12.025 4.95Q7 16.9 7 24q0 7.05 4.975 12.025Q16.95 41 24.05 41ZM24 24Z" />
                </svg>
                <span className="ml-2 text-sm font-light">{error}</span>
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 px-4 py-2 flex items-center justify-center gap-2.5 rounded-full bg-secondary text-white outline-none"
            >
              <span className="text-base font-medium">
                {typeof otpTimer === "number" && otpTimer > 0 ? "Verify OTP" : "Resend OTP"}
              </span>
              {loading && (
                <svg
                  className="shrink-0 h-6 w-6 animate-spin"
                  fill="currentColor"
                  viewBox="0 0 50 50"
                >
                  <path d="M41.9 23.9c-.3-6.1-4-11.8-9.5-14.4-6-2.7-13.3-1.6-18.3 2.6-4.8 4-7 10.5-5.6 16.6 1.3 6 6 10.9 11.9 12.5 7.1 2 13.6-1.4 17.6-7.2-3.6 4.8-9.1 8-15.2 6.9-6.1-1.1-11.1-5.7-12.5-11.7-1.5-6.4 1.5-13.1 7.2-16.4 5.9-3.4 14.2-2.1 18.1 3.7 1 1.4 1.7 3.1 2 4.8.3 1.4.2 2.9.4 4.3.2 1.3 1.3 3 2.8 2.1 1.3-.8 1.2-2.5 1.1-3.8 0-.4.1.7 0 0z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={cancelOTP}
              className="block mx-auto mt-4 text-primary text-base font-medium outline-none"
            >
              Cancel
            </button>
          </form>
        </>
      ) : (
        <>
          <h1 className="text-2xl text-secondary font-semibold">Get Started</h1>
          <h4 className="mt-3 text-darkgray font-light">
            Login or sign up by simply verifying your phone number
          </h4>
          <form onSubmit={sendOTP} className="my-6">
            <input
              className="block w-full px-4 py-2 rounded-md outline-none border border-lightgray bg-white text-primary text-base"
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="Enter Phone Number"
              value={phoneNumber}
              onChange={e => {
                if (e.target.value.length <= 10) setPhoneNumber(e.target.value);
              }}
            />
            <input
              className="block w-full mt-4 px-4 py-2 rounded-md outline-none border border-lightgray bg-white text-primary text-base"
              id="clientCode"
              name="clientCode"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              placeholder="Enter Client Code"
              value={clientCode}
              onChange={e => setClientCode(e.target.value.trim().toUpperCase())}
            />
            {error.length > 0 && (
              <p className="mt-4 p-3 rounded-md flex items-center bg-error/10 text-error">
                <svg className="shrink-0 h-5 w-5" viewBox="0 0 48 48" fill="currentColor">
                  <path d="M24 34q.7 0 1.175-.475.475-.475.475-1.175 0-.7-.475-1.175Q24.7 30.7 24 30.7q-.7 0-1.175.475-.475.475-.475 1.175 0 .7.475 1.175Q23.3 34 24 34Zm-1.35-7.65h3V13.7h-3ZM24 44q-4.1 0-7.75-1.575-3.65-1.575-6.375-4.3-2.725-2.725-4.3-6.375Q4 28.1 4 23.95q0-4.1 1.575-7.75 1.575-3.65 4.3-6.35 2.725-2.7 6.375-4.275Q19.9 4 24.05 4q4.1 0 7.75 1.575 3.65 1.575 6.35 4.275 2.7 2.7 4.275 6.35Q44 19.85 44 24q0 4.1-1.575 7.75-1.575 3.65-4.275 6.375t-6.35 4.3Q28.15 44 24 44Zm.05-3q7.05 0 12-4.975T41 23.95q0-7.05-4.95-12T24 7q-7.05 0-12.025 4.95Q7 16.9 7 24q0 7.05 4.975 12.025Q16.95 41 24.05 41ZM24 24Z" />
                </svg>
                <span className="ml-2 text-sm font-light">{error}</span>
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 px-4 py-2 flex items-center justify-center gap-2.5 rounded-full bg-secondary text-white outline-none"
            >
              <span className="text-base font-medium">Send OTP</span>
              {loading && (
                <svg
                  className="shrink-0 h-6 w-6 animate-spin"
                  fill="currentColor"
                  viewBox="0 0 50 50"
                >
                  <path d="M41.9 23.9c-.3-6.1-4-11.8-9.5-14.4-6-2.7-13.3-1.6-18.3 2.6-4.8 4-7 10.5-5.6 16.6 1.3 6 6 10.9 11.9 12.5 7.1 2 13.6-1.4 17.6-7.2-3.6 4.8-9.1 8-15.2 6.9-6.1-1.1-11.1-5.7-12.5-11.7-1.5-6.4 1.5-13.1 7.2-16.4 5.9-3.4 14.2-2.1 18.1 3.7 1 1.4 1.7 3.1 2 4.8.3 1.4.2 2.9.4 4.3.2 1.3 1.3 3 2.8 2.1 1.3-.8 1.2-2.5 1.1-3.8 0-.4.1.7 0 0z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => setHelpModalOpen(true)}
              className="block mx-auto mt-4 text-primary text-base font-medium outline-none"
            >
              Need Help?
            </button>
            <Dialog
              open={isHelpModalOpen}
              onClose={() => setHelpModalOpen(false)}
              className="fixed inset-0 bg-black/50 p-6 flex items-center justify-center"
            >
              <Dialog.Panel className="relative p-4 bg-white rounded-lg">
                <Dialog.Title as="h2" className="text-secondary text-xl font-semibold">
                  Need Help?
                </Dialog.Title>
                <Dialog.Description
                  as="a"
                  href="mailto:connect@careplix.com"
                  className="mt-5 flex items-center justify-between gap-6 p-3 rounded-lg bg-secondary/20"
                >
                  <div className="text-base">
                    <h4 className="text-primary">Email:</h4>
                    <h6 className="text-primary/70">connect@careplix.com</h6>
                  </div>
                  <svg className="h-8 w-8 text-primary" viewBox="0 0 48 48" fill="currentColor">
                    <path d="M7 40q-1.2 0-2.1-.9Q4 38.2 4 37V11q0-1.2.9-2.1Q5.8 8 7 8h34q1.2 0 2.1.9.9.9.9 2.1v26q0 1.2-.9 2.1-.9.9-2.1.9Zm17-15.1L7 13.75V37h34V13.75Zm0-3L40.8 11H7.25ZM7 13.75V11v26Z" />
                  </svg>
                </Dialog.Description>
                <Dialog.Description
                  as="a"
                  href="tel:033-41816533"
                  className="mt-5 flex items-center justify-between gap-6 p-3 rounded-lg bg-secondary/20"
                >
                  <div className="text-base">
                    <h4 className="text-primary">Phone:</h4>
                    <h6 className="text-primary/70">033-41816533</h6>
                  </div>
                  <svg className="h-8 w-8 text-primary" viewBox="0 0 48 48" fill="currentColor">
                    <path d="M39.75 42q-6.1 0-12.125-3T16.8 31.2Q12 26.4 9 20.375 6 14.35 6 8.25q0-.95.65-1.6Q7.3 6 8.25 6h7q.7 0 1.225.475.525.475.675 1.275l1.35 6.3q.1.7-.025 1.275t-.525.975l-5 5.05q2.8 4.65 6.275 8.1Q22.7 32.9 27.1 35.3l4.75-4.9q.5-.55 1.15-.775.65-.225 1.3-.075l5.95 1.3q.75.15 1.25.75T42 33v6.75q0 .95-.65 1.6-.65.65-1.6.65Zm-28.3-23.4 4.05-4.1L14.35 9H9q0 1.95.6 4.275t1.85 5.325ZM29.9 36.75q2.05.95 4.45 1.55 2.4.6 4.65.7v-5.35l-5.15-1.05ZM11.45 18.6ZM29.9 36.75Z" />
                  </svg>
                </Dialog.Description>
                <button
                  type="button"
                  onClick={() => setHelpModalOpen(false)}
                  className="absolute top-4 right-4 bg-primary text-white font-semibold rounded-full p-[5px]"
                >
                  <span className="sr-only">Close Modal</span>
                  <svg className="h-5 w-5" viewBox="0 0 48 48" fill="currentColor">
                    <path d="m12.45 37.65-2.1-2.1L21.9 24 10.35 12.45l2.1-2.1L24 21.9l11.55-11.55 2.1 2.1L26.1 24l11.55 11.55-2.1 2.1L24 26.1Z" />
                  </svg>
                </button>
              </Dialog.Panel>
            </Dialog>
          </form>
        </>
      )}
    </div>
  );
};

export default LoginPage;
